/**
 * Chats actions — CRUD for stored Deal-Analyst conversations, plus the
 * scenario-aware deletion that honours "chat & scenario are a unit".
 */
import { backendNode, backendPy } from "System/system";
import store from "System/reduxStore";
import { deleteScenario } from "Redux/data/actions";

// Load the chat list for the current project (lightweight rows).
export const loadChats = () => async (dispatch) => {
  const { id } = store.getState().project;
  if (!id) return;
  try {
    const res = await backendNode("chats", "list", { projectid: id });
    dispatch({ type: "SET_CHATS", chats: res.data?.chats || [] });
  } catch (e) {
    dispatch({ type: "SET_CHATS", chats: [] });
  }
};

// Upsert a chat (auto-save). `chat` carries the full messages + owned scenarios;
// the slice keeps only the lightweight row.
export const saveChat = (chat) => async (dispatch) => {
  const { id, uid } = { id: store.getState().project.id, uid: store.getState().user.uid };
  await backendNode("chats", "save", {
    chatId: chat.chatId,
    projectid: id,
    uid,
    title: chat.title || "",
    preview: chat.preview || "",
    messagesString: JSON.stringify(chat.messages || []),
    scenariosString: JSON.stringify(chat.scenarios || []),
  });
  dispatch({
    type: "UPSERT_CHAT",
    chat: {
      chatId: chat.chatId,
      title: chat.title || "",
      preview: chat.preview || "",
      scenarios: chat.scenarios || [],
      updatedAt: Date.now(),
      createdAt: chat.createdAt || Date.now(),
    },
  });
};

// Load a full chat (messages + scenarios) for continuation.
export const loadChat = async (chatId) => {
  const res = await backendNode("chats", "load", { chatId });
  return res.data || null;
};

// Ask the backend for a short title from the conversation start.
export const generateTitle = async (messages) => {
  try {
    const res = await backendPy("chat", "title", { messages: JSON.stringify(messages) });
    return res.data?.title || "";
  } catch (e) {
    return "";
  }
};

// Delete a chat and the scenarios it owns — but keep any scenario still
// referenced by another stored chat. Returns the names of scenarios removed.
export const deleteChatAndScenarios = (chatId) => async (dispatch) => {
  const chats = store.getState().chats.list;
  const target = chats.find((c) => c.chatId === chatId);
  const owned = (target?.scenarios || []).map((s) => ({ id: s.id, name: s.name }));

  const referencedElsewhere = new Set();
  chats
    .filter((c) => c.chatId !== chatId)
    .forEach((c) => (c.scenarios || []).forEach((s) => referencedElsewhere.add(s.id)));

  const removed = [];
  for (const s of owned) {
    if (!referencedElsewhere.has(s.id)) {
      await dispatch(deleteScenario(s.id)); // removes data dir + project entry + results
      removed.push(s.name || s.id);
    }
  }

  try {
    await backendNode("chats", "erase", { chatId });
  } catch (e) {
    // best-effort; still drop from the list
  }
  dispatch({ type: "REMOVE_CHAT", chatId });
  return removed;
};

// Signal the ChatPanel to load & open a chat chosen from history.
export const requestOpenChat = (chatId) => ({ type: "SET_PENDING_OPEN", chatId });
export const clearPendingOpen = () => ({ type: "CLEAR_PENDING_OPEN" });
